import {
  ENERGY_BACKUP_POWUR_OS_EQUIPMENT_ID,
  EV_CHARGER_POWUR_OS_EQUIPMENT_ID,
  INVERTER_POWUR_OS_EQUIPMENT_ID,
  MODULE_POWUR_OS_EQUIPMENT_ID,
} from "@logic/lib/powur-os"
import { atom, DefaultValue, selector, selectorFamily } from "recoil"

import type { SystemType } from "~/graphql/client"
import { selectPresence } from "~/utils/recoil"

import { MOUNT_SELECTION_OPTIONS } from "./defaults"
import { type NewEquipment as EquipmentTypeInferredFromZod } from "../designs/design-model"
import { designLocalState } from "../designs/design-state"
import { isTpoProductSelectedState } from "../loan-products/tpo-loan-product"
import { proposalLocalState } from "../proposal/proposal-state"

export const allEquipmentForSiteState = atom<EquipmentTypeInferredFromZod[]>({
  key: `allEquipmentForSite`,
  default: [],
})

export const equipmentTypeLookupState = selector<
  Record<number, string> & Record<string, number>
>({
  key: `equipmentTypeLookup`,
  get: ({ get }) => {
    const allEquipmentForSite = get(allEquipmentForSiteState)
    const initialValue: Record<number, string> & Record<string, number> = {}
    const equipmentTypeLookup = allEquipmentForSite.reduce((acc, equipment) => {
      if (equipment.typeId && equipment.type) {
        acc[equipment.typeId] = equipment.type
        acc[equipment.type.toLowerCase()] = equipment.typeId
      }

      return acc
    }, initialValue)

    return equipmentTypeLookup
  },
})

export const availableNonPrimaryEquipmentTypeIndex = selector<Set<number>>({
  key: `availableNonPrimaryEquipmentTypes`,
  get: ({ get }) => {
    const allEquipmentForSite = get(allEquipmentForSiteState)
    const nonPrimaryEquipmentTypes = allEquipmentForSite
      .filter(
        (item) =>
          item.typeId !== MODULE_POWUR_OS_EQUIPMENT_ID &&
          item.typeId !== INVERTER_POWUR_OS_EQUIPMENT_ID &&
          item.typeId !== ENERGY_BACKUP_POWUR_OS_EQUIPMENT_ID,
      )
      .map((item) => item.typeId)
    const uniqueNonPrimaryEquipmentTypes = new Set(nonPrimaryEquipmentTypes)

    return uniqueNonPrimaryEquipmentTypes
  },
})

const findByTypeEquipmentForSiteState = selectorFamily<
  EquipmentTypeInferredFromZod[],
  number
>({
  key: `findByTypeEquipmentForSite`,
  get:
    (equipmentTypeId) =>
    ({ get }) => {
      const allEquipment = get(allEquipmentForSiteState)

      return allEquipment.filter((item) => item.typeId === equipmentTypeId)
    },
})

export const solarModulesForSiteState = findByTypeEquipmentForSiteState(
  MODULE_POWUR_OS_EQUIPMENT_ID,
)
export const invertersForSiteState = findByTypeEquipmentForSiteState(
  INVERTER_POWUR_OS_EQUIPMENT_ID,
)
export const batteriesForSiteState = findByTypeEquipmentForSiteState(
  ENERGY_BACKUP_POWUR_OS_EQUIPMENT_ID,
)

export const solarModulesArePresentState = selectPresence(
  solarModulesForSiteState,
)

export const invertersArePresentState = selectPresence(invertersForSiteState)

export const batteriesArePresentState = selector<boolean>({
  key: `batteriesArePresent`,
  get: ({ get }) => {
    const batteries = get(batteriesForSiteState)
    const allEquipment = get(allEquipmentForSiteState)

    return batteries.length > 0 || allEquipment.some((item) => item.isBattery)
  },
})

export const selectedMountState = selector<string[]>({
  key: `selectedMount`,
  get: ({ get }) => {
    const { systemType } = get(proposalLocalState)

    return [
      Object.keys(MOUNT_SELECTION_OPTIONS).find(
        (key) =>
          MOUNT_SELECTION_OPTIONS[
            key as keyof typeof MOUNT_SELECTION_OPTIONS
          ] === systemType,
      ) ?? ``,
    ] as string[]
  },
  set: ({ set }, newValue) => {
    if (newValue instanceof DefaultValue) return
    set(proposalLocalState, (cur) => ({
      ...cur,
      systemType: MOUNT_SELECTION_OPTIONS[
        newValue[0] as keyof typeof MOUNT_SELECTION_OPTIONS
      ] as SystemType | null,
    }))
  },
})

export const inverterBrandOptionsState = selector<string[]>({
  key: `inverterBrandOptions`,
  get: ({ get }) => {
    const inverters = get(invertersForSiteState)
    const names: string[] = [...new Set(inverters.map((i) => i.manufacturer))]

    return names
  },
})

export const selectedInverterBrandState = selector<string[]>({
  key: `selectedInverterBrand`,
  get: ({ get }) => {
    const { inverterManufacturer } = get(designLocalState)

    return [inverterManufacturer ?? ``]
  },
  set: ({ set }, newValue) => {
    if (newValue instanceof DefaultValue) return
    set(designLocalState, (cur) => ({
      ...cur,
      inverterManufacturer: newValue[0],
    }))
  },
})

const isCompatibleWithInverter = (
  equipment: EquipmentTypeInferredFromZod,
  inverterManufacturerId: number | undefined,
): boolean => {
  if (inverterManufacturerId === undefined) {
    return true
  }

  const compatibleIds =
    equipment.evChargerData?.compatibleInverterManufacturerIds

  return !compatibleIds?.length || compatibleIds.includes(inverterManufacturerId)
}

const findNonPrimaryEquipmentOptionsState = selectorFamily<
  EquipmentTypeInferredFromZod[],
  { filterByTpo?: boolean }
>({
  key: `nonPrimaryEquipmentOptions`,
  get:
    (options) =>
    ({ get }) => {
      const allEquipment = get(allEquipmentForSiteState)
      const nonPrimaryEquipment = allEquipment.filter((item) => item.typeId)

      const selectedInverterManufacturerId =
        (
          get(designLocalState).inverter as unknown as {
            manufacturerId?: number
          }
        )?.manufacturerId ??
        (
          get(designLocalState).inverter as unknown as {
            powurOsManufacturerId?: number
          }
        )?.powurOsManufacturerId

      const compatibleEquipment = nonPrimaryEquipment.filter((equipment) =>
        isCompatibleWithInverter(equipment, selectedInverterManufacturerId),
      )

      const isTpoSelected = get(isTpoProductSelectedState)

      if (!isTpoSelected || options.filterByTpo === false) {
        return compatibleEquipment
      }

      return compatibleEquipment.filter((equipment) => {
        return equipment.typeId !== EV_CHARGER_POWUR_OS_EQUIPMENT_ID
      })
    },
})

export const findNonPrimaryEquipmentChosenState = selector<
  EquipmentTypeInferredFromZod[]
>({
  key: `nonPrimaryEquipmentChosen`,
  get: ({ get }) => {
    const design = get(designLocalState)
    const equipmentOptions = get(
      findNonPrimaryEquipmentOptionsState({
        filterByTpo: false,
      }),
    )

    return equipmentOptions.filter((equipment) =>
      design.designEquipment?.some(({ equipment: dEquip }) => {
        return dEquip?.id === equipment.id
      }),
    )
  },
})
